/**
 * Mandar mensagem a um contato pelo caminho de verdade.
 *
 * É o mesmo do "Enviar mensagem" do cadastro: abre (ou reaproveita) a conversa
 * no canal escolhido e envia por ela. Mensagem em lote e aviso de mudança no
 * agendamento diziam "enviada" sem chamar nada — aqui a resposta é a que o
 * servidor deu, inclusive quando a mensagem ficou na fila.
 */

import { dados } from '@/data/porta';
import type { Mensagem } from '@/data/tipos';

export interface Destinatario {
    contatoId: string;
    nome: string;
}

export interface ResultadoDoEnvio extends Destinatario {
    status: Mensagem['status'] | 'erro';
    erro?: string;
}

/** `{nome}` no texto vira o primeiro nome da pessoa. */
export function personalizar(texto: string, nome: string): string {
    const primeiro = nome.trim().split(/\s+/)[0] ?? nome;
    return texto.replace(/\{nome\}/g, primeiro);
}

export async function enviarAoContato(contatoId: string, canalId: string, texto: string): Promise<Mensagem> {
    if (!texto.trim()) throw new Error('A mensagem está vazia.');
    const conversa = await dados().abrirConversaCom(contatoId, canalId);
    return dados().enviarMensagem(conversa, texto);
}

/**
 * Um de cada vez, e cada um com o seu resultado.
 *
 * Em sequência de propósito: o provedor de WhatsApp limita a taxa, e disparar
 * tudo junto faria o meio da lista falhar. Uma falha não interrompe o resto.
 */
export async function enviarParaVarios(destinatarios: Destinatario[], canalId: string, texto: string): Promise<ResultadoDoEnvio[]> {
    const resultados: ResultadoDoEnvio[] = [];
    for (const d of destinatarios) {
        try {
            const m = await enviarAoContato(d.contatoId, canalId, personalizar(texto, d.nome));
            resultados.push({ ...d, status: m.status });
        } catch (e) {
            resultados.push({ ...d, status: 'erro', erro: e instanceof Error ? e.message : 'Falha ao enviar.' });
        }
    }
    return resultados;
}

export interface ResumoDoEnvio {
    titulo: string;
    descricao: string;
    tom: 'success' | 'warning' | 'danger';
}

/** O que dizer depois: saiu, ficou na fila ou falhou — contados separadamente. */
export function resumirEnvio(resultados: ResultadoDoEnvio[]): ResumoDoEnvio {
    const saiu = resultados.filter((r) => r.status === 'enviada' || r.status === 'entregue' || r.status === 'lida').length;
    const naFila = resultados.filter((r) => r.status === 'pendente').length;
    const falhou = resultados.filter((r) => r.status === 'erro' || r.status === 'falhou');
    const partes = [
        saiu ? `${saiu} ${saiu === 1 ? 'enviada' : 'enviadas'}` : '',
        naFila ? `${naFila} na fila do canal` : '',
        falhou.length ? `${falhou.length} com falha${falhou[0]?.erro ? ` (${falhou[0].erro})` : ''}` : '',
    ].filter(Boolean);

    if (falhou.length === resultados.length) {
        return { titulo: 'Nenhuma mensagem saiu', descricao: partes.join(' · '), tom: 'danger' };
    }
    if (falhou.length || naFila) {
        return { titulo: 'Envio com pendências', descricao: partes.join(' · '), tom: 'warning' };
    }
    return {
        titulo: saiu === 1 ? 'Mensagem enviada' : `${saiu} mensagens enviadas`,
        descricao: resultados.slice(0, 3).map((r) => r.nome).join(', ') + (resultados.length > 3 ? '…' : ''),
        tom: 'success',
    };
}
